"use strict";

exports.__esModule = true;
exports.default = void 0;
var _react = _interopRequireWildcard(require("react"));
var _context = require("../../context");
var _IndicatorButtons = require("../IndicatorButtons");
var _ = require(".");
var _enums = require("../../types/enums");
var _hooks = require("../../hooks");
function _getRequireWildcardCache(nodeInterop) { if (typeof WeakMap !== "function") return null; var cacheBabelInterop = new WeakMap(); var cacheNodeInterop = new WeakMap(); return (_getRequireWildcardCache = function (nodeInterop) { return nodeInterop ? cacheNodeInterop : cacheBabelInterop; })(nodeInterop); }
function _interopRequireWildcard(obj, nodeInterop) { if (!nodeInterop && obj && obj.__esModule) { return obj; } if (obj === null || typeof obj !== "object" && typeof obj !== "function") { return { default: obj }; } var cache = _getRequireWildcardCache(nodeInterop); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (key !== "default" && Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj.default = obj; if (cache) { cache.set(obj, newObj); } return newObj; }
// constants
const POLLING_INTERVAL = process.env.GATSBY_PREVIEW_POLL_INTERVAL ? parseInt(process.env.GATSBY_PREVIEW_POLL_INTERVAL) : 2000;
const PAGE_DATA_RETRY_LIMIT = 60;
const Indicator = () => {
  var _buildInfo$siteInfo, _buildInfo$currentBui, _buildInfo$siteInfo2;
  const [loaded, setLoaded] = (0, _react.useState)(false);
  const timeoutRef = (0, _react.useRef)(null);
  const shouldPoll = (0, _react.useRef)(false);
  const refreshNeeded = (0, _react.useRef)(false);
  const trackedInitialLoad = (0, _react.useRef)(false);
  const latestCheckedBuild = (0, _react.useRef)(null);
  const {
    contentSyncInfo,
    currentBuildId,
    usingContentSync,
    setCurrentBuildStatus,
    setBuildInfo,
    setContentSyncInfo,
    setManifestInfo,
    trackEvent
  } = (0, _react.useContext)(_context.IndicatorContext);
  const {
    buildInfo,
    refetch: refetchBuildInfo
  } = (0, _hooks.useBuildInfo)();
  const {
    data: pageData,
    errorMessage: pageDataErrorMessage,
    refetch: refetchPageData
  } = (0, _hooks.usePageData)({
    immediate: false
  });
  const nodeManifestPollArgs = {
    contentLoaderInfo: {
      orgId: buildInfo === null || buildInfo === void 0 ? void 0 : (_buildInfo$siteInfo = buildInfo.siteInfo) === null || _buildInfo$siteInfo === void 0 ? void 0 : _buildInfo$siteInfo.orgId,
      previewBuildStatus: buildInfo === null || buildInfo === void 0 ? void 0 : (_buildInfo$currentBui = buildInfo.currentBuild) === null || _buildInfo$currentBui === void 0 ? void 0 : _buildInfo$currentBui.buildStatus,
      previewUrl:
      // for local dev / debugging
      process.env.GATSBY_PREVIEW_URL ||
      // for production
      window.location.origin
    },
    manifestId: (contentSyncInfo === null || contentSyncInfo === void 0 ? void 0 : contentSyncInfo.manifestId) || ``,
    sourcePluginName: (contentSyncInfo === null || contentSyncInfo === void 0 ? void 0 : contentSyncInfo.pluginName) || ``,
    siteId: (buildInfo === null || buildInfo === void 0 ? void 0 : (_buildInfo$siteInfo2 = buildInfo.siteInfo) === null || _buildInfo$siteInfo2 === void 0 ? void 0 : _buildInfo$siteInfo2.siteId) || ``,
    shouldPoll: !!usingContentSync
  };
  const {
    redirectUrl: nodeManifestRedirectUrl,
    errorMessage: nodeManifestErrorMessage
  } = (0, _hooks.useNodeManifestPoll)(nodeManifestPollArgs);

  // methods
  const getContentSyncInfoFromURL = () => {
    const urlSearchParams = new URLSearchParams(window.location.search);

    // manifestId and pluginName are shortened to keep the url concise
    const {
      mid: manifestId,
      plgn: pluginName
    } = JSON.parse(window.atob(urlSearchParams.get(`csync`) || ``) || `{}`);
    if (!manifestId || !pluginName) {
      return null;
    }
    return {
      manifestId,
      pluginName
    };
  };
  const hasPageDataChanged = async () => {
    if (currentBuildId !== latestCheckedBuild.current) {
      let counter = 0;
      let changed = false;
      while (!changed && counter <= PAGE_DATA_RETRY_LIMIT) {
        const loadedPageData = pageData;
        await refetchPageData();
        if (pageDataErrorMessage) {
          return {
            changed: false,
            errorMessage: pageDataErrorMessage
          };
        }
        counter++;
        changed = loadedPageData !== pageData;
        if (!changed) {
          await new Promise(resolve => setTimeout(resolve, POLLING_INTERVAL));
        }
      }
      latestCheckedBuild.current = currentBuildId;
      return {
        changed,
        errorMessage: null
      };
    }
    return {
      changed: false,
      errorMessage: null
    };
  };
  const pollData = () => {
    refetchBuildInfo();
    if (shouldPoll.current) {
      timeoutRef.current = setTimeout(pollData, POLLING_INTERVAL);
    }
  };

  // effect hooks
  (0, _react.useEffect)(() => {
    const contentSyncInfoFromURL = getContentSyncInfoFromURL();
    if (contentSyncInfoFromURL) {
      setContentSyncInfo(contentSyncInfoFromURL);
    } else {
      refetchPageData();
    }
    setTimeout(() => {
      shouldPoll.current = true;
      pollData();
    }, 1000);
    setLoaded(true);
    return function cleanup() {
      shouldPoll.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);
  (0, _react.useEffect)(() => {
    setBuildInfo(buildInfo);
    if (buildInfo !== null && buildInfo !== void 0 && buildInfo.siteInfo && buildInfo !== null && buildInfo !== void 0 && buildInfo.latestBuild && !trackedInitialLoad.current) {
      trackEvent({
        eventType: _enums.EventType.PREVIEW_INDICATOR_LOADED,
        name: `indicator loaded`
      });
      trackedInitialLoad.current = true;
    }
  }, [buildInfo]);
  (0, _react.useEffect)(() => {
    const updateBuildData = async () => {
      const oldBuildId = currentBuildId;
      if (buildInfo !== null && buildInfo !== void 0 && buildInfo.currentBuild) {
        const {
          currentBuild
        } = buildInfo;
        if ([_enums.BuildStatus.BUILDING, _enums.BuildStatus.ERROR, _enums.BuildStatus.QUEUED, _enums.BuildStatus.UPLOADING].includes(currentBuild.buildStatus)) {
          setCurrentBuildStatus(currentBuild.buildStatus);
        } else if (oldBuildId) {
          var _buildInfo$latestBuil;
          if (oldBuildId === currentBuild.id) {
            setCurrentBuildStatus(_enums.BuildStatus.UP_TO_DATE);
          } else if (oldBuildId !== (buildInfo === null || buildInfo === void 0 ? void 0 : (_buildInfo$latestBuil = buildInfo.latestBuild) === null || _buildInfo$latestBuil === void 0 ? void 0 : _buildInfo$latestBuil.id) && currentBuild.buildStatus === _enums.BuildStatus.SUCCESS) {
            if (refreshNeeded.current) {
              setCurrentBuildStatus(_enums.BuildStatus.SUCCESS);
            } else if (!usingContentSync) {
              const {
                changed: pageDataChanged,
                errorMessage
              } = await hasPageDataChanged();
              if (errorMessage) {
                setBuildInfo({
                  ...buildInfo,
                  errorMessage
                });
                setCurrentBuildStatus(_enums.BuildStatus.ERROR);
              } else if (pageDataChanged) {
                // Force a "This page has updated message" until a page is refreshed
                refreshNeeded.current = true;
                // Build updated, data for this specific page has changed!
                setCurrentBuildStatus(_enums.BuildStatus.SUCCESS);
              } else {
                // Build updated, data for this specific page has NOT changed, no need to refresh content.
                setCurrentBuildStatus(_enums.BuildStatus.UP_TO_DATE);
              }
            }
          }
        }
      } else {
        setCurrentBuildStatus(_enums.BuildStatus.UP_TO_DATE);
      }
    };
    updateBuildData();
  }, [buildInfo === null || buildInfo === void 0 ? void 0 : buildInfo.currentBuild, buildInfo === null || buildInfo === void 0 ? void 0 : buildInfo.latestBuild, buildInfo]);
  (0, _react.useEffect)(() => {
    if (nodeManifestErrorMessage) {
      setManifestInfo({
        errorMessage: nodeManifestErrorMessage
      });
    } else if (nodeManifestRedirectUrl) {
      setManifestInfo({
        redirectUrl: nodeManifestRedirectUrl
      });
      // Force a "This page has updated message" until the page is refreshed
      refreshNeeded.current = true;
      setCurrentBuildStatus(_enums.BuildStatus.SUCCESS);
    }
  }, [nodeManifestRedirectUrl, nodeManifestErrorMessage]);
  return /*#__PURE__*/_react.default.createElement(_.IndicatorWrapper, {
    loaded: loaded
  }, /*#__PURE__*/_react.default.createElement(_IndicatorButtons.GatsbyIndicatorButton, null), /*#__PURE__*/_react.default.createElement(_IndicatorButtons.InfoIndicatorButton, null), /*#__PURE__*/_react.default.createElement(_IndicatorButtons.LinkIndicatorButton, null));
};
const PreviewIndicator = () => /*#__PURE__*/_react.default.createElement(_context.IndicatorProvider, null, /*#__PURE__*/_react.default.createElement(Indicator, null));
var _default = PreviewIndicator;
exports.default = _default;